import semver from 'semver';
import { DeviceEntryProps, DeviceType, GatewayEntry, TBeaconEntry } from '@ixicare/resources';
import {
  BEACON_LAST_SEEN_THRESHOLD,
  INSTALL_BATT_THRESHOLD,
  LAST_SEEN_THRESHOLD,
  MIN_SW_VERSION_PER_DEVICE_TYPE,
} from './constants';
import { DBG, DBGWithType, Status, UnifiedDBG } from '../components/DeviceHealthTable/types';

export const mapDBGWithType = (dbgs: DBG[], type?: DeviceType): DBGWithType[] =>
  dbgs.map((x) => {
    if (type) return { ...x, type };

    if ('deviceType' in x) return { ...x, type: x.deviceType as DeviceType };

    if ('mcuVersion' in x) return { ...x, type: DeviceType.GATEWAY };

    if ('calibrationFactor' in x) return { ...x, type: DeviceType.BEACON };

    return { ...x, type: DeviceType.EQUIPMENT_TAG };
  });

interface SwInfoOptions {
  swVersion?: string;
  mcuVersion?: string;
  bleVersion?: string;
}

interface DetermineStatusOptions {
  deviceType: DeviceType;
  batteryLevel?: number;
  lastSeen?: Date;
  swInfo?: SwInfoOptions;
}

const determineSoftwareOutdated = (deviceType: DeviceType, swInfo?: SwInfoOptions): Status => {
  if ([DeviceType.EVIEW, DeviceType.TWIG].includes(deviceType)) {
    return Status.GREEN;
  }

  if (!swInfo) {
    return Status.OFFLINE; // In other deviceTypes, no software info means no status update, which is considered offline.
  }

  const minSwVersion = MIN_SW_VERSION_PER_DEVICE_TYPE[deviceType];

  if (swInfo.swVersion) {
    const version = semver.coerce(swInfo.swVersion.replace('tequinity/', ''));
    return version && semver.lt(version, minSwVersion.swVersion) ? Status.OUTDATED : Status.GREEN;
  }

  if (swInfo.mcuVersion && swInfo.bleVersion) {
    return semver.lt(swInfo.mcuVersion, minSwVersion.mcuVersion) ||
      semver.lt(swInfo.bleVersion, minSwVersion.bleVersion)
      ? Status.OUTDATED
      : Status.GREEN;
  } else {
    return Status.OUTDATED; // If 1 of the 2 software version is missing, we consider it outdated.
  }
};

// returns the status of the device, in order of severity.
// Will probably start keeping multiple statuses in the future, but for now, we only return one status.
const determineStatus = ({ batteryLevel, lastSeen, deviceType, swInfo }: DetermineStatusOptions) => {
  if (lastSeen === undefined) {
    return Status.OFFLINE;
  }

  const lastSeenThreshold = deviceType === DeviceType.BEACON ? BEACON_LAST_SEEN_THRESHOLD : LAST_SEEN_THRESHOLD;
  if (lastSeen <= lastSeenThreshold) {
    return Status.MISSING;
  }

  const softwareStatus = determineSoftwareOutdated(deviceType, swInfo);
  if (softwareStatus !== Status.GREEN) {
    return softwareStatus;
  }

  // lowbat for fresh installation
  if (batteryLevel !== undefined && batteryLevel < INSTALL_BATT_THRESHOLD) {
    return Status.LOW_BATTERY;
  }

  return Status.GREEN;
};

export const mapDeviceToUnified = (device: DeviceEntryProps): UnifiedDBG => ({
  id: device.id,
  type: device?.deviceType ?? DeviceType.WATCHERR, // Device should always have a deviceType, not correctly defined in frontend packages.
  macAddress: device.macAddress,
  status: determineStatus({
    batteryLevel: device.batteryLevel,
    lastSeen: device?.status?.updatedOn,
    deviceType: device?.deviceType ?? DeviceType.WATCHERR,
    swInfo: {
      swVersion: device.swVersion,
    },
  }),

  ownerId: device?.ownerId,
  swVersion: device?.swVersion,
  batteryLevel: device?.batteryLevel,
  chargerConnected: device?.chargerConnected,
  description: device?.description,
  planeId: device?.planeId,
  installationCoords: device?.installationCoords,
  created: device?.created,
  updated: device?.updated,
  lastSeen: device?.status?.updatedOn,
});

export const mapBeaconToUnified = (beacon: TBeaconEntry): UnifiedDBG => ({
  id: beacon.id,
  type: DeviceType.BEACON,
  macAddress: beacon.macAddress,
  status: determineStatus({
    batteryLevel: beacon?.batteryLevel,
    lastSeen: beacon?.lastSeen,
    deviceType: DeviceType.BEACON,
    swInfo: {
      swVersion: beacon?.swVersion ?? '1',
    },
  }),

  swVersion: beacon?.swVersion ?? '1',
  description: beacon?.description,
  zoneId: beacon.zoneId,
  planeId: beacon.planeId,
  installationCoords: beacon?.installationCoords,
  created: beacon?.created,
  updated: beacon?.updated,
  lastSeen: beacon?.lastSeen,
});

export const mapGatewayToUnified = (gateway: GatewayEntry): UnifiedDBG => ({
  id: gateway.id,
  type: DeviceType.GATEWAY,
  macAddress: gateway?.macAddress ?? '',
  status: determineStatus({
    lastSeen: gateway?.lastSeen,
    deviceType: DeviceType.GATEWAY,
    swInfo: {
      mcuVersion: gateway?.mcuVersion,
      bleVersion: gateway?.bleVersion,
    },
  }),

  swVersion: gateway?.mcuVersion,
  bleVersion: gateway?.bleVersion,
  description: gateway?.description,
  zoneId: gateway?.zone?.id.toString(),
  planeId: gateway?.planeId,
  installationCoords: gateway?.installationCoords,
  created: gateway?.created,
  updated: gateway?.updated,
  lastSeen: gateway?.lastSeen,
});
